import { useState, useEffect, useCallback } from 'react';
import { hrms } from '../../services/api';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import Modal from '../../components/common/Modal';
import Table from '../../components/common/Table';
import { theme } from '../../theme';

function DeptSection({ title, items, loading, columns, modalContent, modalTitle }) {
  const [showModal, setShowModal] = useState(false);
  return (
    <Card title={title} titleAction={<Button size="sm" variant="primary" onClick={() => setShowModal(true)}>+ Add</Button>}>
      <Table columns={columns} data={items} loading={loading} emptyMessage={`No ${title.toLowerCase()} found`} />
      <Modal isOpen={showModal} onClose={() => setShowModal(false)} title={modalTitle}>
        {modalContent(() => setShowModal(false))}
      </Modal>
    </Card>
  );
}

function Departments() {
  const [departments, setDepartments] = useState([]);
  const [teams, setTeams] = useState([]);
  const [positions, setPositions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editTarget, setEditTarget] = useState(null);
  const [editType, setEditType] = useState('');
  const [saving, setSaving] = useState(false);

  const [deptForm, setDeptForm] = useState({ name: '', code: '', description: '' });
  const [teamForm, setTeamForm] = useState({ name: '', department_id: '', description: '' });
  const [posForm, setPosForm] = useState({ name: '', department_id: '', team_id: '' });

  const [grades, setGrades] = useState([]);
  const [gradeForm, setGradeForm] = useState({ name: '', years_of_experience: '' });
  const [editGradeId, setEditGradeId] = useState(null);
  const [gradeSaving, setGradeSaving] = useState(false);
  const [showGradeModal, setShowGradeModal] = useState(false);

  const [contractors, setContractors] = useState([]);
  const [contractorForm, setContractorForm] = useState({ name: '', address: '', pincode: '', city: '', state: '', gst_number: '' });
  const [editContractorId, setEditContractorId] = useState(null);
  const [contractorSaving, setContractorSaving] = useState(false);
  const [showContractorModal, setShowContractorModal] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [d, t, p, g, c] = await Promise.all([hrms.getDepartments(), hrms.getTeams(), hrms.getPositions(), hrms.getGrades(), hrms.getContractors()]);
      setDepartments(d.data || []);
      setTeams(t.data || []);
      setPositions(p.data || []);
      setGrades(g.data || []);
      setContractors(c.data || []);
    } catch (err) { console.warn('[Departments] Failed to load:', err?.message); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleDeptSave = async () => {
    if (!deptForm.name.trim()) { alert('Department name is required.'); return; }
    setSaving(true);
    try {
      if (editTarget && editType === 'dept') {
        await hrms.updateDepartment(editTarget.id, deptForm);
      } else {
        await hrms.createDepartment(deptForm);
      }
      setDeptForm({ name: '', code: '', description: '' });
      setEditTarget(null); setEditType('');
      load();
    } catch (e) { alert('Failed to save department: ' + (e.response?.data?.detail || e.message)); }
    finally { setSaving(false); }
  };

  const handleTeamSave = async () => {
    if (!teamForm.name.trim()) { alert('Team name is required.'); return; }
    if (!teamForm.department_id) { alert('Please select a department.'); return; }
    setSaving(true);
    try {
      if (editTarget && editType === 'team') {
        await hrms.updateTeam(editTarget.id, teamForm);
      } else {
        await hrms.createTeam(teamForm);
      }
      setTeamForm({ name: '', department_id: '', description: '' });
      setEditTarget(null); setEditType('');
      load();
    } catch (e) { alert('Failed to save team: ' + (e.response?.data?.detail || e.message)); }
    finally { setSaving(false); }
  };

  const handlePosSave = async () => {
    if (!posForm.name.trim()) { alert('Position title is required.'); return; }
    setSaving(true);
    try {
      const posPayload = {
        ...posForm,
        department_id: posForm.department_id ? Number(posForm.department_id) : null,
        team_id:       posForm.team_id       ? Number(posForm.team_id)       : null,
      };
      if (editTarget && editType === 'pos') {
        await hrms.updatePosition(editTarget.id, posPayload);
      } else {
        await hrms.createPosition(posPayload);
      }
      setPosForm({ name: '', department_id: '', team_id: '' });
      setEditTarget(null); setEditType('');
      load();
    } catch (e) { alert('Failed to save position: ' + (e.response?.data?.detail || e.message)); }
    finally { setSaving(false); }
  };

  const handleContractorSave = async () => {
    if (!contractorForm.name.trim()) { alert('Contractor name is required.'); return; }
    setContractorSaving(true);
    try {
      if (editContractorId) {
        await hrms.updateContractor(editContractorId, contractorForm);
      } else {
        await hrms.createContractor(contractorForm);
      }
      setContractorForm({ name: '', address: '', pincode: '', city: '', state: '', gst_number: '' });
      setEditContractorId(null);
      setShowContractorModal(false);
      load();
    } catch (e) { alert('Failed to save contractor: ' + (e.response?.data?.detail || e.message)); }
    finally { setContractorSaving(false); }
  };

  const handleGradeSave = async () => {
    if (!gradeForm.name.trim()) { alert('Grade name is required.'); return; }
    setGradeSaving(true);
    try {
      if (editGradeId) {
        await hrms.updateGrade(editGradeId, gradeForm);
      } else {
        await hrms.createGrade(gradeForm);
      }
      setGradeForm({ name: '', years_of_experience: '' });
      setEditGradeId(null);
      setShowGradeModal(false);
      load();
    } catch (e) { alert('Failed to save grade: ' + (e.response?.data?.detail || e.message)); }
    finally { setGradeSaving(false); }
  };

  const inp = { width: '100%', padding: '0.6rem 0.875rem', border: `1.5px solid ${theme.border}`, borderRadius: '0.5rem', fontSize: '0.875rem', color: theme.text, outline: 'none', marginBottom: '0.75rem', boxSizing: 'border-box' };
  const lbl = { display: 'block', fontSize: '0.8rem', fontWeight: 600, color: theme.text, marginBottom: '0.35rem' };

  const deptCols = [
    { header: 'Name', key: 'name' },
    { header: 'Code', key: 'code' },
    { header: 'Description', key: 'description', render: (v) => v || '—' },
    {
      header: 'Actions', key: 'id', align: 'right',
      render: (_, row) => (
        <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
          <Button size="sm" variant="outline" onClick={() => { setEditTarget(row); setEditType('dept'); setDeptForm({ name: row.name, code: row.code || '', description: row.description || '' }); }}>Edit</Button>
          <Button size="sm" variant="danger" onClick={async () => { if (window.confirm('Delete department?')) { try { await hrms.deleteDepartment(row.id); load(); } catch { alert('Failed to delete department.'); } } }}>Delete</Button>
        </div>
      ),
    },
  ];

  const teamCols = [
    { header: 'Name', key: 'name' },
    { header: 'Department', key: 'department_name', render: (v, row) => v || departments.find((d) => d.id === row.department_id)?.name || '—' },
    { header: 'Description', key: 'description', render: (v) => v || '—' },
    {
      header: 'Actions', key: 'id', align: 'right',
      render: (_, row) => (
        <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
          <Button size="sm" variant="outline" onClick={() => { setEditTarget(row); setEditType('team'); setTeamForm({ name: row.name, department_id: row.department_id || '', description: row.description || '' }); }}>Edit</Button>
          <Button size="sm" variant="danger" onClick={async () => { if (window.confirm('Delete team?')) { try { await hrms.deleteTeam(row.id); load(); } catch { alert('Failed to delete team.'); } } }}>Delete</Button>
        </div>
      ),
    },
  ];

  const posCols = [
    { header: 'Title', key: 'name' },
    { header: 'Department', key: 'department_name', render: (v, row) => v || departments.find((d) => d.id === row.department_id)?.name || '—' },
    { header: 'Team', key: 'team_id', render: (v, row) => row.team_name || teams.find((t) => t.id === (v || row.team_id))?.name || '—' },
    {
      header: 'Actions', key: 'id', align: 'right',
      render: (_, row) => (
        <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
          <Button size="sm" variant="outline" onClick={() => { setEditTarget(row); setEditType('pos'); setPosForm({ name: row.name || '', department_id: row.department_id || '', team_id: row.team_id || '' }); }}>Edit</Button>
          <Button size="sm" variant="danger" onClick={async () => { if (window.confirm('Delete position?')) { try { await hrms.deletePosition(row.id); load(); } catch { alert('Failed to delete position.'); } } }}>Delete</Button>
        </div>
      ),
    },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
      <div>
        <h1 style={{ fontSize: '1.5rem', fontWeight: 700, color: theme.text, margin: 0 }}>Departments & Structure</h1>
        <p style={{ color: theme.textMuted, fontSize: '0.875rem', margin: '0.25rem 0 0' }}>Manage departments, teams, and positions</p>
      </div>

      {/* Departments */}
      <Card title="Departments" titleAction={
        <Button size="sm" variant="primary" onClick={() => { setEditTarget(null); setEditType('dept'); setDeptForm({ name: '', code: '', description: '' }); }}>
          + Add Department
        </Button>
      }>
        <Table columns={deptCols} data={departments} loading={loading} emptyMessage="No departments" />
      </Card>

      {/* Teams */}
      <Card title="Teams" titleAction={
        <Button size="sm" variant="primary" onClick={() => { setEditTarget(null); setEditType('team'); setTeamForm({ name: '', department_id: '', description: '' }); }}>
          + Add Team
        </Button>
      }>
        <Table columns={teamCols} data={teams} loading={loading} emptyMessage="No teams" />
      </Card>

      {/* Positions */}
      <Card title="Positions" titleAction={
        <Button size="sm" variant="primary" onClick={() => { setEditTarget(null); setEditType('pos'); setPosForm({ name: '', department_id: '', team_id: '' }); }}>
          + Add Position
        </Button>
      }>
        <Table columns={posCols} data={positions} loading={loading} emptyMessage="No positions" />
      </Card>

      {/* Grade Master */}
      <Card title="Grade Master" titleAction={
        <Button size="sm" variant="primary" onClick={() => { setGradeForm({ name: '', years_of_experience: '' }); setEditGradeId(null); setShowGradeModal(true); }}>+ Add Grade</Button>
      }>
        <Table
          columns={[
            { header: 'Grade Name', key: 'name' },
            { header: 'Years of Experience', key: 'years_of_experience', render: (v) => v || '—' },
            {
              header: 'Actions', key: 'id', align: 'right',
              render: (id, row) => (
                <div style={{ display: 'flex', gap: '0.4rem', justifyContent: 'flex-end' }}>
                  <Button size="sm" variant="outline" onClick={() => { setGradeForm({ name: row.name, years_of_experience: row.years_of_experience || '' }); setEditGradeId(id); setShowGradeModal(true); }}>Edit</Button>
                  <Button size="sm" variant="danger" onClick={async () => { if (window.confirm('Delete grade?')) { await hrms.deleteGrade(id); load(); } }}>Delete</Button>
                </div>
              ),
            },
          ]}
          data={grades}
          loading={loading}
          emptyMessage="No grades defined"
        />
      </Card>

      {/* Grade Modal */}
      <Modal
        isOpen={showGradeModal}
        onClose={() => setShowGradeModal(false)}
        title={editGradeId ? 'Edit Grade' : 'Add Grade'}
        footer={<><Button variant="outlineGray" onClick={() => setShowGradeModal(false)}>Cancel</Button><Button variant="primary" loading={gradeSaving} onClick={handleGradeSave}>Save</Button></>}
      >
        <label style={lbl}>Grade Name *</label>
        <input style={inp} value={gradeForm.name} onChange={e => setGradeForm(p => ({ ...p, name: e.target.value }))} placeholder="e.g. Junior, Senior, Lead" />
        <label style={lbl}>Years of Experience</label>
        <input style={inp} value={gradeForm.years_of_experience} onChange={e => setGradeForm(p => ({ ...p, years_of_experience: e.target.value }))} placeholder="e.g. 0-2 years" />
      </Modal>

      {/* Contractor Master */}
      <Card title="Contractor Master" titleAction={
        <Button size="sm" variant="primary" onClick={() => { setContractorForm({ name: '', address: '', pincode: '', city: '', state: '', gst_number: '' }); setEditContractorId(null); setShowContractorModal(true); }}>+ Add Contractor</Button>
      }>
        <Table
          columns={[
            { header: 'Name', key: 'name' },
            { header: 'City', key: 'city', render: v => v || '—' },
            { header: 'State', key: 'state', render: v => v || '—' },
            { header: 'GST Number', key: 'gst_number', render: v => v || '—' },
            {
              header: 'Actions', key: 'id', align: 'right',
              render: (id, row) => (
                <div style={{ display: 'flex', gap: '0.4rem', justifyContent: 'flex-end' }}>
                  <Button size="sm" variant="outline" onClick={() => { setContractorForm({ name: row.name, address: row.address || '', pincode: row.pincode || '', city: row.city || '', state: row.state || '', gst_number: row.gst_number || '' }); setEditContractorId(id); setShowContractorModal(true); }}>Edit</Button>
                  <Button size="sm" variant="danger" onClick={async () => { if (window.confirm('Delete contractor?')) { await hrms.deleteContractor(id); load(); } }}>Delete</Button>
                </div>
              ),
            },
          ]}
          data={contractors}
          loading={loading}
          emptyMessage="No contractors defined"
        />
      </Card>

      {/* Contractor Modal */}
      <Modal
        isOpen={showContractorModal}
        onClose={() => setShowContractorModal(false)}
        title={editContractorId ? 'Edit Contractor' : 'Add Contractor'}
        footer={<><Button variant="outlineGray" onClick={() => setShowContractorModal(false)}>Cancel</Button><Button variant="primary" loading={contractorSaving} onClick={handleContractorSave}>Save</Button></>}
      >
        <label style={lbl}>Contractor Name *</label>
        <input style={inp} value={contractorForm.name} onChange={e => setContractorForm(p => ({ ...p, name: e.target.value }))} placeholder="e.g. ABC Contractors Pvt Ltd" />
        <label style={lbl}>Address</label>
        <input style={inp} value={contractorForm.address} onChange={e => setContractorForm(p => ({ ...p, address: e.target.value }))} placeholder="Street / Building" />
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
          <div>
            <label style={lbl}>City</label>
            <input style={{ ...inp, marginBottom: 0 }} value={contractorForm.city} onChange={e => setContractorForm(p => ({ ...p, city: e.target.value }))} placeholder="Chennai" />
          </div>
          <div>
            <label style={lbl}>Pincode</label>
            <input style={{ ...inp, marginBottom: 0 }} value={contractorForm.pincode} onChange={e => setContractorForm(p => ({ ...p, pincode: e.target.value }))} placeholder="600001" maxLength={10} />
          </div>
        </div>
        <div style={{ height: '0.75rem' }} />
        <label style={lbl}>State</label>
        <input style={inp} value={contractorForm.state} onChange={e => setContractorForm(p => ({ ...p, state: e.target.value }))} placeholder="Tamil Nadu" />
        <label style={lbl}>GST Number</label>
        <input style={{ ...inp, marginBottom: 0 }} value={contractorForm.gst_number} onChange={e => setContractorForm(p => ({ ...p, gst_number: e.target.value.toUpperCase() }))} placeholder="22AAAAA0000A1Z5" maxLength={15} />
      </Modal>

      {/* Edit/Add Modals */}
      <Modal
        isOpen={editType === 'dept' && editTarget !== undefined && editTarget !== false}
        onClose={() => { setEditType(''); setEditTarget(null); }}
        title={editTarget ? 'Edit Department' : 'Add Department'}
        footer={
          <>
            <Button variant="outlineGray" onClick={() => { setEditType(''); setEditTarget(null); }}>Cancel</Button>
            <Button variant="primary" loading={saving} onClick={handleDeptSave}>Save</Button>
          </>
        }
      >
        <label style={lbl}>Name *</label>
        <input style={inp} value={deptForm.name} onChange={(e) => setDeptForm({ ...deptForm, name: e.target.value })} placeholder="Department name" />
        <label style={lbl}>Code</label>
        <input style={inp} value={deptForm.code} onChange={(e) => setDeptForm({ ...deptForm, code: e.target.value })} placeholder="DEPT001" />
        <label style={lbl}>Description</label>
        <textarea style={{ ...inp, resize: 'vertical', minHeight: '80px' }} value={deptForm.description} onChange={(e) => setDeptForm({ ...deptForm, description: e.target.value })} placeholder="Description..." />
      </Modal>

      <Modal
        isOpen={editType === 'team' && editTarget !== undefined && editTarget !== false}
        onClose={() => { setEditType(''); setEditTarget(null); }}
        title={editTarget ? 'Edit Team' : 'Add Team'}
        footer={
          <>
            <Button variant="outlineGray" onClick={() => { setEditType(''); setEditTarget(null); }}>Cancel</Button>
            <Button variant="primary" loading={saving} onClick={handleTeamSave}>Save</Button>
          </>
        }
      >
        <label style={lbl}>Name *</label>
        <input style={inp} value={teamForm.name} onChange={(e) => setTeamForm({ ...teamForm, name: e.target.value })} placeholder="Team name" />
        <label style={lbl}>Department</label>
        <select style={inp} value={teamForm.department_id} onChange={(e) => setTeamForm({ ...teamForm, department_id: e.target.value })}>
          <option value="">Select department</option>
          {departments.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
        </select>
        <label style={lbl}>Description</label>
        <textarea style={{ ...inp, resize: 'vertical', minHeight: '80px' }} value={teamForm.description} onChange={(e) => setTeamForm({ ...teamForm, description: e.target.value })} />
      </Modal>

      <Modal
        isOpen={editType === 'pos' && editTarget !== undefined && editTarget !== false}
        onClose={() => { setEditType(''); setEditTarget(null); }}
        title={editTarget ? 'Edit Position' : 'Add Position'}
        footer={
          <>
            <Button variant="outlineGray" onClick={() => { setEditType(''); setEditTarget(null); }}>Cancel</Button>
            <Button variant="primary" loading={saving} onClick={handlePosSave}>Save</Button>
          </>
        }
      >
        <label style={lbl}>Title *</label>
        <input style={inp} value={posForm.name} onChange={(e) => setPosForm({ ...posForm, name: e.target.value })} placeholder="Software Engineer" />
        <label style={lbl}>Department</label>
        <select style={inp} value={posForm.department_id} onChange={(e) => setPosForm({ ...posForm, department_id: e.target.value, team_id: '' })}>
          <option value="">Select department</option>
          {departments.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
        </select>
        <label style={lbl}>Team</label>
        <select style={inp} value={posForm.team_id} onChange={(e) => setPosForm({ ...posForm, team_id: e.target.value })}>
          <option value="">Select team (optional)</option>
          {teams
            .filter((t) => !posForm.department_id || String(t.department_id) === String(posForm.department_id))
            .map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
        </select>
      </Modal>
    </div>
  );
}

export default Departments;
